import type { AppEvent } from "./types";

/**
 * Check if event is a lesson
 */
export function isLesson(e: AppEvent): boolean {
  return e.kind === "lesson";
}

/**
 * Check if event is a deadline or assignment
 */
export function isDeadlineLike(e: AppEvent): boolean {
  return e.kind === "deadline" || e.kind === "assignment";
}

/**
 * Check if two dates are on the same day
 */
export function sameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

/**
 * Get lessons for today
 */
export function getTodayLessons(events: AppEvent[]): AppEvent[] {
  const today = new Date();
  return events
    .filter(isLesson)
    .filter((e) => sameDay(new Date(e.start), today))
    .sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime());
}

/**
 * Get current lesson (happening now)
 */
export function getCurrentLesson(events: AppEvent[], now: number = Date.now()): AppEvent | null {
  const todayLessons = getTodayLessons(events);
  for (const e of todayLessons) {
    const st = new Date(e.start).getTime();
    const en = e.end ? new Date(e.end).getTime() : st;
    if (now >= st && now <= en) return e;
  }
  return null;
}

/**
 * Get next lesson (upcoming today)
 */
export function getNextLesson(events: AppEvent[], now: number = Date.now()): AppEvent | null {
  const todayLessons = getTodayLessons(events);
  for (const e of todayLessons) {
    const st = new Date(e.start).getTime();
    if (st > now) return e;
  }
  return null;
}

/**
 * Get upcoming deadlines
 */
export function getUpcomingDeadlines(
  events: AppEvent[],
  limit: number = 10,
  now: number = Date.now()
): AppEvent[] {
  return events
    .filter(isDeadlineLike)
    .filter((e) => new Date(e.due || e.start).getTime() > now - 60_000)
    .sort((a, b) => new Date(a.due || a.start).getTime() - new Date(b.due || b.start).getTime())
    .slice(0, limit);
}

/**
 * Filter events by hidden courses
 */
export function filterHiddenCourses(
  events: AppEvent[],
  hiddenCourses: string[],
  normalizeCourseKey: (name?: string) => string
): AppEvent[] {
  if (hiddenCourses.length === 0) return events;
  const hidden = new Set(hiddenCourses);
  return events.filter((e) => {
    const key = e.courseKey || normalizeCourseKey(e.courseName || e.title);
    if (!key) return true;
    const teacherKey = normalizeCourseKey(e.teacher || "");
    if (teacherKey) {
      const scopedKey = `${key}::${teacherKey}`;
      if (hidden.has(scopedKey)) return false;
    }
    return !hidden.has(key);
  });
}
